import React, { useState, useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useTranslation from '@/hooks/useTranslation';
import DateTimePicker from '@react-native-community/datetimepicker';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { DEFAULT_FONT_FAMILY } from '@/constants/config';

const createStyles = (colors, noSpacing = false) => StyleSheet.create({
  dateRangeContainer: {
    paddingHorizontal: noSpacing ? 0 : 8,
    paddingBottom: noSpacing ? 0 : 8,
    gap: 5,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dateLabel: {
    color: colors.text_color_secondary,
    width: 60,
    fontSize: 13,
  },
  dateActionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 6,
  },
  flex1: { flex: 1 },
  rowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: 30,
    paddingVertical: 5,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginVertical: 0,
    width: '100%',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }
    })
  },
  rowButtonText: {
    color: colors.text_color_primary,
    fontSize: 14,
  },
  dropdownHeaderText: {
    color: colors.text_color_tertiary,
    fontSize: 14,
  },
  doneButton: {
    paddingHorizontal: 10,
    paddingBottom: 5,
    marginLeft: 'auto',
    display: 'flex',
    minWidth: 100,
    maxWidth: 100,
  },
});

// Style helpers for DateRangeControl
const DONE_BUTTON_CLEAR_STYLES = { doneButton: { width: 50, backgroundColor: 'transparent' } };
const DONE_BUTTON_WIDE_STYLES = { doneButton: { width: 'auto', flex: 1, marginLeft: 10 } };

const createWebDateInputStyle = (colors) => ({
  flex: 1,
  backgroundColor: 'transparent',
  color: colors.text_color_primary,
  border: `1px solid ${colors.solid_color_tertiary}`,
  fontFamily: DEFAULT_FONT_FAMILY,
  padding: 8,
  borderRadius: 6,
});

const DoneButton = ({ title, onSelect, styles, isSecondary }) => {
  return (
    <View style={styles.doneButton}>
      <DropdownMenuItem title={title} onSelect={onSelect} isSecondary={isSecondary} isTitleCentered />
    </View>
  );
};

// Date range control (start and end date)
// When used inside dropdown menus, keep `useMenuItems` as true so the
// Clear/Done actions are rendered as proper menu items. For inline usage
// (e.g., detailed panels), set `useMenuItems={false}` to avoid depending
// on DropdownMenu menu context.
const DateRangeControl = ({ param, onChange, useMenuItems = true, withoutHeaderText = false, noSpacing = false }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, noSpacing), [colors, noSpacing]);
  const parseDate = (str) => {
    if (!str || typeof str !== 'string') return null;
    const [y, m, d] = str.split('-').map(n => Number(n));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };
  const pad2 = (n) => String(n).padStart(2, '0');
  const fmt = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    return `${y}-${m}-${d}`;
  };

  const current = Array.isArray(param?.value) ? param.value : null;
  const [startDate, setStartDate] = useState(() => (current ? parseDate(current[0]) : null));
  const [endDate, setEndDate] = useState(() => (current ? parseDate(current[1]) : null));
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd] = useState(false);

  // Whenever either date changes, push value up if both are set; otherwise clear
  const propagate = (nextStart, nextEnd) => {
    const formattedStart = nextStart ? fmt(nextStart) : null;
    const formattedEnd = nextEnd ? fmt(nextEnd) : null;
    if (!!formattedStart && !!formattedEnd) {
      onChange([formattedStart, formattedEnd]);
    } else if (!formattedStart && !formattedEnd) {
      onChange(null);
    } else if (!!formattedStart && !formattedEnd) {
      onChange([formattedStart, null]);
    } else if (!formattedStart && !!formattedEnd) {
      onChange([null, formattedEnd]);
    }
  };

  const onStartChange = (_, selectedDate) => {
    setShowStart(false);
    if (selectedDate) {
      const next = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      setStartDate(next);
      propagate(next, endDate);
    }
  };
  const onEndChange = (_, selectedDate) => {
    setShowEnd(false);
    if (selectedDate) {
      const next = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      setEndDate(next);
      propagate(startDate, next);
    }
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    onChange(null);
  };

  const webDateInputStyle = React.useMemo(() => createWebDateInputStyle(colors), [colors]);

  return (
    <View style={styles.dateRangeContainer}>
      {!withoutHeaderText && <Text style={styles.dropdownHeaderText}>{t('chatInput.dateRange.chooseDateRange')}</Text>}
      {/* Start Date Row */}
      <View style={styles.dateRow}>
        <Text style={styles.dateLabel}>{t('chatInput.dateRange.start')}</Text>
        {Platform.OS === 'web' ? (
          <input
            type="date"
            value={startDate ? fmt(startDate) : ''}
            onChange={(e) => {
              const v = e.target.value;
              const d = parseDate(v);
              setStartDate(d);
              propagate(d, endDate);
            }}
            style={webDateInputStyle}
          />
        ) : (
          <>
            <Pressable
              style={[styles.rowButton, styles.flex1]}
              onPress={() => setShowStart(true)}
            >
              <Text style={styles.rowButtonText}>{startDate ? fmt(startDate) : t('chatInput.dateRange.selectStartDate')}</Text>
              <Icon name="calendar-range" size={14} color={colors.text_color_primary} />
            </Pressable>
            {showStart && (
              <DateTimePicker
                value={startDate || new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                onChange={onStartChange}
              />
            )}
          </>
        )}
      </View>

      {/* End Date Row */}
      <View style={styles.dateRow}>
        <Text style={styles.dateLabel}>{t('chatInput.dateRange.end')}</Text>
        {Platform.OS === 'web' ? (
          <input
            type="date"
            value={endDate ? fmt(endDate) : ''}
            onChange={(e) => {
              const v = e.target.value;
              const d = parseDate(v);
              setEndDate(d);
              propagate(startDate, d);
            }}
            style={webDateInputStyle}
          />
        ) : (
          <>
            <Pressable
              style={[styles.rowButton, styles.flex1]}
              onPress={() => setShowEnd(true)}
            >
              <Text style={styles.rowButtonText}>{endDate ? fmt(endDate) : t('chatInput.dateRange.selectEndDate')}</Text>
              <Icon name="calendar-range" size={14} color={colors.text_color_primary} />
            </Pressable>
            {showEnd && (
              <DateTimePicker
                value={endDate || new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                onChange={onEndChange}
              />
            )}
          </>
        )}
      </View>

      {useMenuItems && (
        <View style={styles.dateActionsRow}>
          <DoneButton title={t('common.clear')} onSelect={clearDates} styles={DONE_BUTTON_CLEAR_STYLES} />
          <DoneButton title={t('common.done')} onSelect={() => {}} styles={DONE_BUTTON_WIDE_STYLES} isSecondary />
        </View>
      )}
    </View>
  );
};

export default DateRangeControl;

